// React Query hooks for /api/v1/users/me/*. All return safe defaults when the
// user is anonymous so calling components don't need to branch on auth state
// just to render — they branch only when offering write actions.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCurrentUser, type User } from "@/hooks/useAuth";
import { usePreferences } from "@/store/preferences";

interface SavedSearch {
  query: string;
  label: string | null;
  saved_at: string;
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: "same-origin" });
  if (!res.ok) throw new Error(`${path} → ${res.status}`);
  return res.json();
}

async function send(path: string, method: string, body?: unknown): Promise<unknown> {
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const detail = await res
      .json()
      .then((j) => (typeof j?.detail === "string" ? j.detail : null))
      .catch(() => null);
    throw new Error(detail ?? `${path} → ${res.status}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

// ── Settings ────────────────────────────────────────────────────────────────────

interface UserSettingsUpdate {
  include_images?: boolean;
}

export function useUpdateUserSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: UserSettingsUpdate) => {
      return (await send("/api/v1/users/me/settings", "PATCH", patch)) as User;
    },
    onSuccess: (user) => {
      qc.setQueryData(["auth", "me"], user);
      // Search results depend on include_images.
      qc.invalidateQueries({ queryKey: ["posts"] });
    },
  });
}

/**
 * Effective include-images preference for the current request.
 *
 * Resolves to: logged-in user's column value (if signed in) else the
 * zustand-persisted anonymous toggle. The server still has authority —
 * if the operator hasn't enabled image indexing it ignores anything we
 * send. This hook is the source of truth the search hook reads.
 */
export function useEffectiveIncludeImages(): boolean {
  const { data: user } = useCurrentUser();
  const anon = usePreferences((s) => s.includeImages);
  return user ? user.include_images : anon;
}

// ── Blacklist ───────────────────────────────────────────────────────────────────

export function useUserBlacklist() {
  const { data: user } = useCurrentUser();
  return useQuery<string[]>({
    queryKey: ["user", "blacklist", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const body = await get<{ tags: string[] }>("/api/v1/users/me/blacklist");
      return body.tags;
    },
    enabled: !!user,
    staleTime: 60_000,
  });
}

export function useReplaceBlacklist() {
  const qc = useQueryClient();
  const { data: user } = useCurrentUser();
  return useMutation({
    mutationFn: async (tags: string[]) => {
      const body = (await send(
        "/api/v1/users/me/blacklist",
        "PUT",
        { tags }
      )) as { tags: string[] };
      return body.tags;
    },
    onSuccess: (tags) => {
      qc.setQueryData(["user", "blacklist", user?.id], tags);
      // Search results depend on the blacklist.
      qc.invalidateQueries({ queryKey: ["posts"] });
    },
  });
}

// ── Saved searches ──────────────────────────────────────────────────────────────

export function useSavedSearches() {
  const { data: user } = useCurrentUser();
  return useQuery<SavedSearch[]>({
    queryKey: ["user", "saved-searches", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const body = await get<{ searches: SavedSearch[] }>(
        "/api/v1/users/me/saved-searches"
      );
      return body.searches;
    },
    enabled: !!user,
    staleTime: 60_000,
  });
}

export function useAddSavedSearch() {
  const qc = useQueryClient();
  const { data: user } = useCurrentUser();
  return useMutation({
    mutationFn: async (input: { query: string; label?: string | null }) => {
      return (await send("/api/v1/users/me/saved-searches", "POST", input)) as SavedSearch;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["user", "saved-searches", user?.id] });
    },
  });
}

export function useRemoveSavedSearch() {
  const qc = useQueryClient();
  const { data: user } = useCurrentUser();
  return useMutation({
    mutationFn: async (query: string) => {
      await send(
        `/api/v1/users/me/saved-searches/${encodeURIComponent(query)}`,
        "DELETE"
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["user", "saved-searches", user?.id] });
    },
  });
}

// ── Favorites ───────────────────────────────────────────────────────────────────

export function useFavoriteIds() {
  const { data: user } = useCurrentUser();
  return useQuery<Set<number>>({
    queryKey: ["user", "favorite-ids", user?.id],
    queryFn: async () => {
      if (!user) return new Set<number>();
      const body = await get<{ ids: number[] }>("/api/v1/users/me/favorites/ids");
      return new Set(body.ids);
    },
    enabled: !!user,
    staleTime: 60_000,
  });
}

export function useToggleFavorite() {
  const qc = useQueryClient();
  const { data: user } = useCurrentUser();
  return useMutation({
    mutationFn: async ({ postId, favorite }: { postId: number; favorite: boolean }) => {
      if (favorite) {
        await send(`/api/v1/users/me/favorites/${postId}`, "POST");
      } else {
        await send(`/api/v1/users/me/favorites/${postId}`, "DELETE");
      }
      return { postId, favorite };
    },
    // Optimistic update: flip immediately so the heart feels instant.
    onMutate: async ({ postId, favorite }) => {
      const key = ["user", "favorite-ids", user?.id];
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Set<number>>(key);
      const next = new Set(previous ?? []);
      if (favorite) next.add(postId);
      else next.delete(postId);
      qc.setQueryData(key, next);
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous !== undefined) {
        qc.setQueryData(["user", "favorite-ids", user?.id], ctx.previous);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["user", "favorites", user?.id] });
    },
  });
}

interface FavoritePost {
  post_id: number;
  added_at: string;
  post: import("@/hooks/useSearch").Post;
}

export function useFavoritesPage(page = 1, limit = 40) {
  const { data: user } = useCurrentUser();
  return useQuery<{ favorites: FavoritePost[]; total: number }>({
    queryKey: ["user", "favorites", user?.id, page, limit],
    queryFn: async () =>
      get(`/api/v1/users/me/favorites?page=${page}&limit=${limit}`),
    enabled: !!user,
    staleTime: 30_000,
  });
}
